import React, { useEffect, useState } from "react";
import { getAdminOrders } from "../../services/adminService";
import { Link } from "react-router-dom";

const STATUS_LABELS = {
	pending: "En attente",
	confirmed: "Confirmée",
	preparing: "En préparation",
	ready: "Prête",
	completed: "Terminée",
	cancelled: "Annulée",
};

const PAYMENT_LABELS = {
	paid: "Payé",
	pending: "En attente",
	unpaid: "Non payé",
	failed: "Échoué",
	refunded: "Remboursé",
};

const STATUS_BADGES = {
	pending: "bg-amber-50 text-amber-700 border border-amber-200",
	confirmed: "bg-emerald-50 text-emerald-700 border border-emerald-200",
	preparing: "bg-orange-50 text-orange-700 border border-orange-200",
	ready: "bg-green-50 text-green-700 border border-green-200",
	completed: "bg-emerald-100 text-emerald-800 border border-emerald-200",
	cancelled: "bg-red-50 text-red-700 border border-red-200",

	paid: "bg-emerald-50 text-emerald-700 border border-emerald-200",
	unpaid: "bg-red-50 text-red-700 border border-red-200",
	failed: "bg-red-50 text-red-700 border border-red-200",
	refunded: "bg-stone-100 text-stone-700 border border-stone-200",
};

const formatPrice = (amount) =>
	new Intl.NumberFormat("fr-FR", {
		style: "currency",
		currency: "CHF",
	}).format(Number(amount || 0));

const formatDate = (date) => {
	if (!date) return "—";

	return new Intl.DateTimeFormat("fr-FR", {
		day: "2-digit",
		month: "2-digit",
		year: "numeric",
		hour: "2-digit",
		minute: "2-digit",
	}).format(new Date(date));
};

const getCustomerName = (order) => {
	return (
		[order.customer_first_name, order.customer_last_name]
			.filter(Boolean)
			.join(" ")
			.trim() || "Client inconnu"
	);
};

const AdminOrdersPage = () => {
	const [orders, setOrders] = useState([]);
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		const loadOrders = async () => {
			try {
				const data = await getAdminOrders();
				setOrders(data || []);
			} catch (error) {
				console.error("Erreur chargement commandes :", error.message);
			} finally {
				setLoading(false);
			}
		};

		loadOrders();
	}, []);

	const getLabel = (map, key, fallback = "—") => map[key] || fallback;

	const getBadgeClass = (status) =>
		STATUS_BADGES[status] ||
		"bg-stone-100 text-stone-700 border border-stone-200";

	return (
		<div className="mx-auto max-w-7xl px-4 pb-10 pt-32">
			<div className="mb-10">
				<h1 className="text-4xl font-semibold tracking-tight text-[#2A2A2A]">
					Gestion des commandes
				</h1>
				<p className="mt-3 text-lg text-[#7A766F]">
					Consulte l’ensemble des commandes avec un affichage clair et
					structuré.
				</p>
			</div>

			<div className="overflow-hidden rounded-[28px] border border-[#E7E0D4] bg-white shadow-[0_8px_30px_rgba(0,0,0,0.04)]">
				<div className="overflow-x-auto">
					<table className="w-full min-w-[1250px] border-separate border-spacing-0">
						<thead>
							<tr className="bg-[#FCFAF7]">
								<th className="px-8 py-5 text-left text-xs font-semibold uppercase tracking-[0.14em] text-[#8A857D]">
									Commande
								</th>
								<th className="px-6 py-5 text-left text-xs font-semibold uppercase tracking-[0.14em] text-[#8A857D]">
									Client
								</th>
								<th className="px-6 py-5 text-left text-xs font-semibold uppercase tracking-[0.14em] text-[#8A857D]">
									Email
								</th>
								<th className="px-6 py-5 text-left text-xs font-semibold uppercase tracking-[0.14em] text-[#8A857D]">
									Paiement
								</th>
								<th className="px-6 py-5 text-left text-xs font-semibold uppercase tracking-[0.14em] text-[#8A857D]">
									Statut
								</th>
								<th className="px-6 py-5 text-left text-xs font-semibold uppercase tracking-[0.14em] text-[#8A857D]">
									Total
								</th>
								<th className="px-8 py-5 text-left text-xs font-semibold uppercase tracking-[0.14em] text-[#8A857D]">
									Date
								</th>
								<th className="px-6 py-5 text-right text-xs font-semibold uppercase tracking-[0.14em] text-[#8A857D]">
									Actions
								</th>
							</tr>
						</thead>

						<tbody>
							{!loading &&
								orders.map((order, index) => {
									const items = order.order_items || [];

									return (
										<tr
											key={order.id}
											className={
												index !== orders.length - 1
													? "border-b border-[#F1ECE4]"
													: ""
											}
										>
											<td className="px-8 py-7 align-top">
												<p className="text-sm font-semibold text-[#2A2A2A]">
													#{order.id}
												</p>

												{items.length > 0 && (
													<div className="mt-4 space-y-2">
														{items.map((item) => (
															<div
																key={item.id}
																className="flex justify-between rounded-xl bg-[#FAF8F4] px-4 py-3"
															>
																<span className="text-sm font-medium">
																	{item.product_name}
																</span>
																<span className="text-sm text-[#7A766F]">
																	× {item.quantity}
																</span>
															</div>
														))}
													</div>
												)}
											</td>

											<td className="px-6 py-7 align-top font-medium text-[#2A2A2A]">
												{getCustomerName(order)}
											</td>

											<td className="px-6 py-7 align-top text-sm text-[#5F5A54]">
												{order.customer_email || "—"}
											</td>

											<td className="px-6 py-7 align-top">
												<span
													className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${getBadgeClass(order.payment_status)}`}
												>
													{getLabel(
														PAYMENT_LABELS,
														order.payment_status,
														"En attente",
													)}
												</span>
											</td>

											<td className="px-6 py-7 align-top">
												<span
													className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${getBadgeClass(order.status)}`}
												>
													{getLabel(STATUS_LABELS, order.status, "En attente")}
												</span>
											</td>

											<td className="px-6 py-7 align-top font-semibold text-[#2A2A2A]">
												{formatPrice(order.total_amount)}
											</td>

											<td className="px-8 py-7 align-top text-sm text-[#5F5A54]">
												{formatDate(order.created_at)}
											</td>
											<td className="px-6 py-7 align-top text-right">
												<Link
													to={`/commande/${order.id}`}
													className="inline-flex items-center rounded-full bg-[#2A2A2A] px-4 py-2 text-xs font-medium text-white transition hover:bg-black"
												>
													Gérer la commande
												</Link>
											</td>
										</tr>
									);
								})}
						</tbody>
					</table>
				</div>

				{loading && (
					<div className="px-8 py-8 text-sm text-[#7A766F]">
						Chargement des commandes...
					</div>
				)}

				{!loading && orders.length === 0 && (
					<div className="px-8 py-8 text-sm text-[#7A766F]">
						Aucune commande pour le moment.
					</div>
				)}
			</div>
		</div>
	);
};

export default AdminOrdersPage;
